import React, { useMemo, useState } from 'react';
import Button from '../Button';
import EmptyState from '../EmptyState';
import { READING, READING_WORDING as RW, reading } from '../../domain/reading';
import { Menu, MenuItem } from '../Menu';
import { invoiceEmailWord } from '../../domain/invoiceEmail';
import { useMoneyFormat, useDateFormat } from '../../context/LocaleContext';
import { familyDisplayName } from '../../domain/people';
import {
  failedRequestText, invoiceListIsEmpty, invoiceListOrder, invoicePeriodText, requestFamilyText,
  skippedBadgeText,
} from '../../domain/invoice';
import s from './families.module.css';
import t from '../SimpleTable.module.css';

// Invoices, plus the requests that have not become invoices yet.
//
// Generation happens in a Cloud Function, so a request sits in the list as
// "Creating" until it lands — and a failure stays visible rather than vanishing,
// because an invoice that silently never arrived is the worst outcome here.
//
// ── MMS's order and MMS's dates ────────────────────────────────────────────
//
// MMS opens this tab on `Invoice Date ↓` and writes its dates in the studio's
// short pattern — `8/29/2026`, `9/1/2026 - 9/30/2026` for a US studio. Each
// group goes newest first (`invoiceListOrder`, the core's rule the phones
// share) and each date through the studio's format. The Creating and failed
// rows stay above the invoices: they are the ones a teacher is waiting on.
//
// ── Skipped rows (#579) ────────────────────────────────────────────────────
//
// A family a run passed over because it owed nothing, with Skip creating the
// invoice chosen, keeps a row: its date drawn as the invoice link would be but
// disabled, and "Skipped" where the amount would be. MMS lists nothing for it;
// a run that skipped every family would leave a list that looked as though the
// run had vanished. `invoicing::skip_is_listed` in the core has the rule. The
// rows are finished results, so they sort among the invoices by date.
//
// ── The row menu (#622) ────────────────────────────────────────────────────
//
// MMS's invoice row menu offers View, Email, Download PDF, Override Status,
// Add a Payment, Archive and Delete. Ours offers View and Email, which opens
// the Send Invoice drawer (SendInvoiceDrawer.jsx); the PDF is the invoice
// document's Open PDF. An invoice New Invoice was asked to email and could not
// — nobody on the family has an address — says so under its amount
// (`notEmailed`).
export default function InvoiceList({
  invoices, pendingRequests, failedRequests, skippedRows = [], families, onNewInvoice, onOpenInvoice,
  onEmailInvoice = null, notEmailed = null, read = true, failed = false, onAddStudent = null,
}) {
  const [rowMenuId, setRowMenuId] = useState(null);
  const closeMenu = () => setRowMenuId(null);
  const formatMoney = useMoneyFormat();
  const formatDate = useDateFormat();
  const period = (start, end) => invoicePeriodText(formatDate(start) || start, formatDate(end) || end);
  const pendingShown = useMemo(() => invoiceListOrder(pendingRequests), [pendingRequests]);
  const failedShown = useMemo(() => invoiceListOrder(failedRequests), [failedRequests]);
  const invoicesShown = useMemo(() => invoiceListOrder([
    ...invoices.map((inv) => ({ ...inv, skippedKey: null })),
    ...skippedRows.map((row) => ({ ...row, skippedKey: row.key })),
  ]), [invoices, skippedRows]);
  const familyName = (id) => {
    const f = families.find((x) => x.id === id);
    return f ? familyDisplayName(f) : '–';
  };

  // Read, and nothing in it (#629): the invoices and the requests windows
  // have answered — `read`, from `useInvoices().loading`.
  const empty = invoiceListIsEmpty({
    invoices, pending: pendingRequests, failed: failedRequests, skipped: skippedRows,
  });

  return (
    <>
      <div className={s.bar}>
        <Button onClick={onNewInvoice} disabled={families.length === 0}>+ New Invoice</Button>
      </div>

      {empty && !read ? (
        <EmptyState reading={failed ? READING.failed : READING.unread} />
      ) : empty ? (
        <div className={t.wrap}>
          {/* MMS's empty Invoices tab offers + Create New Invoice, and so does
              this once there is a family to invoice. Before there is one, New
              Invoice stays shut, since an invoice is a family's, and the empty
              state offers + Add Student, the only way a family account comes
              to exist (#630). What MMS's drawer does with no family is
              unchecked. */}
          {families.length ? (
            <EmptyState
              reading={reading(read, 0)}
              title={RW['invoices.emptyTitle']}
              description={RW['invoices.emptyDescription']}
              actionLabel="+ New Invoice"
              onAction={onNewInvoice}
            />
          ) : (
            <EmptyState
              reading={reading(read, 0)}
              title={RW['invoices.emptyTitle']}
              description={RW['invoices.noFamiliesDescription']}
              actionLabel={onAddStudent ? '+ Add Student' : undefined}
              onAction={onAddStudent}
            />
          )}
        </div>
      ) : (
        <div className={t.wrap}>
          <table className={t.table}>
            <thead>
              <tr>
                <th>Invoice Date</th><th>Family</th><th>Date Range</th><th>Invoice Amount</th>
                {onEmailInvoice ? <th aria-label={invoiceEmailWord('rowMenu')} /> : null}
              </tr>
            </thead>
            <tbody>
              {pendingShown.map((r) => (
                <tr key={r.id}>
                  <td>{formatDate(r.invoiceDate) || r.invoiceDate}</td>
                  <td>{requestFamilyText(r.familyIds, familyName)}</td>
                  <td>{period(r.periodStart, r.periodEnd)}</td>
                  <td><span className={s.badgePending}>Creating ⟳</span></td>
                  {onEmailInvoice ? <td /> : null}
                </tr>
              ))}

              {failedShown.map((r) => (
                <tr key={r.id}>
                  <td>{formatDate(r.invoiceDate) || r.invoiceDate}</td>
                  <td colSpan={2} className={s.failed}>
                    {failedRequestText(r.error)}
                  </td>
                  <td />
                  {onEmailInvoice ? <td /> : null}
                </tr>
              ))}

              {invoicesShown.map((inv) => (inv.skippedKey ? (
                <tr key={inv.skippedKey} data-skipped="true">
                  <td>
                    <button className={s.link} disabled>
                      {formatDate(inv.invoiceDate) || inv.invoiceDate}
                    </button>
                  </td>
                  <td>{familyName(inv.familyId)}</td>
                  <td>{period(inv.periodStart, inv.periodEnd)}</td>
                  <td><span className={s.badgeSkipped}>{skippedBadgeText()}</span></td>
                  {onEmailInvoice ? <td /> : null}
                </tr>
              ) : (
                <tr key={inv.id}>
                  <td>
                    <button className={s.link} onClick={() => onOpenInvoice(inv.id)}>
                      {formatDate(inv.invoiceDate) || inv.invoiceDate}
                    </button>
                  </td>
                  <td>{familyName(inv.familyId)}</td>
                  <td>{period(inv.periodStart, inv.periodEnd)}</td>
                  <td>
                    {formatMoney(inv.totalDueMinor)}
                    {notEmailed?.has(inv.id) ? <div className={s.failed}>{invoiceEmailWord('notEmailed')}</div> : null}
                  </td>
                  {onEmailInvoice ? (
                    <td className={s.rowMenuCell}>
                      <Menu
                        open={rowMenuId === inv.id}
                        onClose={closeMenu}
                        trigger={(
                          <button
                            type="button"
                            className={s.kebab}
                            aria-label={invoiceEmailWord('rowMenu')}
                            onClick={() => setRowMenuId((cur) => (cur === inv.id ? null : inv.id))}
                          >
                            ⋮
                          </button>
                        )}
                      >
                        <MenuItem onClick={() => { closeMenu(); onOpenInvoice(inv.id); }}>
                          {invoiceEmailWord('rowView')}
                        </MenuItem>
                        <MenuItem onClick={() => { closeMenu(); onEmailInvoice(invoices.find((x) => x.id === inv.id) || inv); }}>
                          {invoiceEmailWord('rowEmail')}
                        </MenuItem>
                      </Menu>
                    </td>
                  ) : null}
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
